import * as MemoizeUtils from "sh-application/utility/MemoizeUtils";
import * as moment from "moment";
import type { DateTimeInput } from "sh-application/utility/DateTimeFormatter";
import StringsStore from "sh-strings/store";
import TimeZoneUtils from "sh-application/utility/TimeZoneUtils";
import { DayOfWeek } from "@fluentui/react";
import { Moment } from "moment";
import { utcToZonedTime, zonedTimeToUtc } from "date-fns-tz";
import { addDays, addMonths, addWeeks, isSameDay as dateFnsIsSameDay, startOfDay } from "date-fns";

export interface TimeRange {
    startTime: Moment;
    endTime: Moment;
}

const ALL_DAY_END_DATE_THRESHOLD_MS = 1000;
const NUMBER_OF_MINUTES_IN_ONE_DAY = 1440;
const MS_PER_MINUTE = 60000;
const MS_PER_HOUR = 3600000;
const MS_PER_DAY = 86400000;

/** Convert DateTimeInput to native Date. TODO: Delete once Moment is removed and all inputs are Date. */
function toNativeDate(input: DateTimeInput): Date {
    return input instanceof Date ? input : input?.toDate();
}

/** Get the team timezone */
function getDisplayTimezone(): string {
    return TimeZoneUtils.displayTimezone;
}

/** Cached Intl.DateTimeFormat per timezone for getTimezoneOffsetMs */
let _offsetFmtCache: { tz: string; fmt: Intl.DateTimeFormat } | null = null;

function getOffsetFormatter(timezone: string): Intl.DateTimeFormat {
    if (!_offsetFmtCache || _offsetFmtCache.tz !== timezone) {
        _offsetFmtCache = {
            tz: timezone,
            fmt: new Intl.DateTimeFormat("en-US", {
                timeZone: timezone,
                year: "numeric", month: "2-digit", day: "2-digit",
                hour: "2-digit", minute: "2-digit", second: "2-digit",
                hour12: false
            })
        };
    }
    return _offsetFmtCache.fmt;
}

/**
 * Get the UTC offset for a timezone at a specific instant, in milliseconds.
 * Uses Intl.DateTimeFormat which correctly handles DST transition boundaries
 * (date-fns-tz getTimezoneOffset has a known bug on DST transition days).
 */
function getTimezoneOffsetMs(timezone: string, date: Date): number {
    const parts: Record<string, string> = {};
    for (const p of getOffsetFormatter(timezone).formatToParts(date)) {
        if (p.type !== "literal") {
            parts[p.type] = p.value;
        }
    }
    const localMs = Date.UTC(+parts.year, +parts.month - 1, +parts.day,
        +parts.hour % 24, +parts.minute, +parts.second);
    return localMs - date.getTime();
}

export default class DateUtils {

    /**
     * Comparator for sorting objects with startTimes
     * @param firstItem
     * @param secondItem
     */
    public static startTimeComparator(firstItem: {startTime: DateTimeInput}, secondItem: {startTime: DateTimeInput}): number {
        return toNativeDate(firstItem.startTime).getTime() - toNativeDate(secondItem.startTime).getTime();
    }

    /**
     * Compute the last inclusive day for a date range.
     * @param {Moment} endDate The end date of a date range
     * @returns {Moment} Date representing the last day of the date range
     */
    public static getLastDayForEndDate(endDate: Moment) {
        // If the end date ends at midnight, then the last inclusive day is actually the previous day.
        let lastDayDate = endDate.clone().startOf("day");
        if (endDate.isSame(lastDayDate)) {
            lastDayDate.subtract(1, "day");
        }
        return lastDayDate;
    }

    /**
     * @deprecated Use getStartOfDay_New instead. Will be renamed to getStartOfDay after Moment removal.
     */
    public static getStartOfDay(date: Moment): Moment {
        if (date) {
            return date.clone().startOf("day");
        }

        return null;
    }

    /**
     * Returns true if the specified datetime is at the start of the day (ie, midnight)
     */
    public static isStartOfDay(date: DateTimeInput): boolean {
        if (!date) {
            return false;
        }
        const d = toNativeDate(date);
        const zoned = utcToZonedTime(d, getDisplayTimezone());
        return zoned.getTime() === startOfDay(zoned).getTime();
    }

    /**
     * Returns true if the specified datetime is at the end of the day (not including midnight of the next day)
     */
    public static isEndOfDay(date: DateTimeInput): boolean {
        if (!date) {
            return false;
        }
        const d = toNativeDate(date);
        const zoned = utcToZonedTime(d, getDisplayTimezone());
        const zonedMs = zoned.getTime();
        // End of day = 23:59:59.999 in team timezone. Use addDays for DST safety.
        const nextMidnightMs = addDays(startOfDay(zoned), 1).getTime();
        const endOfDayMaxMs = nextMidnightMs - 1;
        const endOfDayMinMs = endOfDayMaxMs - ALL_DAY_END_DATE_THRESHOLD_MS;
        return (endOfDayMinMs <= zonedMs) && (zonedMs <= endOfDayMaxMs);
    }

    /**
     * Returns true if the given time range is an All Day range (eg, 2/1 to 2/5), as opposed to a time range that
     * has specific start and end times (eg, 2/1 9am to 2/5 5pm).
     *
     * All Day events are stored in the service as events that begin at midnight and end slightly before the
     * midnight of a following day.
     *
     * @param {Moment} startDate start of range
     * @param {Moment} endDate end of range
     * @returns {boolean} true if we have an All Day range
     */
    public static isAllDayTimeRange(startDate: DateTimeInput, endDate: DateTimeInput) {
        if (!startDate || !endDate) {
            return false;
        }
        const startD = toNativeDate(startDate);
        const endD = toNativeDate(endDate);

        // Verify start occurs before the end
        if (startD.getTime() >= endD.getTime()) {
            return false;
        }
        // For All Day events, the start time must be at the beginning of the day (in team timezone)
        if (!DateUtils.isStartOfDay(startDate)) {
            return false;
        }
        // Check if end is at midnight (modern all-day) or within threshold of end-of-day (legacy data)
        return DateUtils.isStartOfDay(endDate) || DateUtils.isEndOfDay(endDate);
    }

    /**
     * Determines if the specified time range spans a single day only
     * @param {Moment} startDate
     * @param {Moment} endDate
     * @returns {Boolean}
     */
    public static isTimeRangeForTheSameDay(startDate: DateTimeInput, endDate: DateTimeInput) {
        if (!startDate || !endDate) {
            return false;
        }
        const startD = toNativeDate(startDate);
        const endD = toNativeDate(endDate);
        const timezone = getDisplayTimezone();

        if (dateFnsIsSameDay(utcToZonedTime(startD, timezone), utcToZonedTime(endD, timezone))) {
            return true;
        }
        // If the end is midnight of the following day from the start, that still counts as the same day.
        return endD.getTime() === DateUtils.getEndOfDay(startD).getTime();
    }

    /**
     * Generates an array of dates within the given date range (inclusive)
     */
    public static getDatesInRange(startDateTime: Moment, endDateTime: Moment): Moment[] {
        const numDays = DateUtils.getNumberOfDaysForTimeRangeInclusive(startDateTime, endDateTime);
        const datesInRange: Moment[] = DateUtils.getDatesForNumberOfDays(startDateTime, numDays);
        return datesInRange;
    }

    private static _datesInRangeCache: {[key: string]: Moment[]} = {};
    public static fastGetDatesInRange = MemoizeUtils.memoizeUtility(
        DateUtils.getDatesInRange,
        DateUtils._datesInRangeCache,
        <Moment>(args: Moment[]) => {
            return `${args[0].valueOf()}:${args[1].valueOf()}`;
        }
    );

    /**
     * Generates an array of dates for the specified number of days, starting with the specified start date
     */
    public static getDatesForNumberOfDays(startDateTime: Moment, numDays: number): Moment[] {
        const startDate = startDateTime.clone().startOf('day');
        let currDate = startDate;
        let datesInRange: Moment[] = [];
        for (let i = 0; i < numDays; i++) {
            datesInRange.push(currDate.clone());
            currDate = DateUtils.startOfNextDay(currDate);
        }
        return datesInRange;
    }

    /**
     * Get the inclusive number of days between two dates
     */
    public static getNumberOfDaysForTimeRangeInclusive(startDateTime: Moment, endDateTime: Moment): number {
        const startDate = startDateTime.clone().startOf('day');
        const endDate = endDateTime.clone().endOf('day');
        // Add one to the difference, because Moment.js rounds down, and we want an inclusive count
        const numDaysInclusive = endDate.diff(startDate, 'day') + 1;
        return numDaysInclusive;
    }

    /**
     * Convert a display version of a date in the specified timezone to the actual date.
     * The display version of the date is represented as a Javascript Date in the user's browser timezone.
     * @param {Date} displayDate - display version of a date in the specified timezone
     * @param {String} timezone - timezone that the specified date is displayed as
     * @returns {Date} actual date of the display date
     */
    public static convertDisplayTimeToTimezoneDateTime(displayDate: Date, timezone?: string) {
        if (!displayDate) {
            return null;
        }
        try {
            return zonedTimeToUtc(displayDate, timezone || getDisplayTimezone());
        } catch {
            // Invalid timezone (RangeError) — return the input date as-is (wrong timezone but preserves the instant)
            return displayDate;
        }
    }

    /**
     * Create a Moment with the passed in display time in the given timezone
     * Convert a display version of a date in the specified timezone to the actual date.
     * The display version of the date is represented as a Javascript Date in the user's browser timezone.
     * @param {Date} displayDate - display version of a date in the specified timezone
     * @param {String} timezone - timezone that the specified date is displayed as
     * @returns {Moment} actual date of the display date
     */
    public static convertDisplayTimeToTimezoneMoment(displayDate: Date, timezone: string) {
        let timezoneMoment: Moment;
        if (displayDate) {
            // Use the passed in display date to create a Moment with that display time in the given timezone
            timezoneMoment = moment.tz([displayDate.getFullYear(), displayDate.getMonth(), displayDate.getDate(), displayDate.getHours(), displayDate.getMinutes(), displayDate.getSeconds(), displayDate.getMilliseconds()], timezone);
        }
        return timezoneMoment;
    }

    /**
     * Convert a UTC Date to a display Date whose local getters (.getHours(), etc.) reflect the target timezone.
     * Used to prepare dates for Intl.DateTimeFormat which reads local getters.
     * @param {Date} date - UTC instant
     * @param {String} tz - optional timezone override; defaults to TimeZoneUtils.displayTimezone
     * @returns {Date} display Date with local fields matching the target timezone
     */
    public static toDisplayDate(date: Date, timezone?: string): Date {
        if (!date) {
            return null;
        }
        try {
            const zonedDate = utcToZonedTime(date, timezone || getDisplayTimezone());
            return new Date(zonedDate.getFullYear(), zonedDate.getMonth(), zonedDate.getDate(), zonedDate.getHours(), zonedDate.getMinutes(), zonedDate.getSeconds(), zonedDate.getMilliseconds());
        } catch {
            // Invalid timezone (RangeError) — fall back to browser-local components so callers still render a date
            return new Date(date.getFullYear(), date.getMonth(), date.getDate(), date.getHours(), date.getMinutes(), date.getSeconds(), date.getMilliseconds());
        }
    }

    /**
     * @deprecated Use toDisplayDate instead. This alias exists for backward compatibility during migration.
     */
    public static convertTimezoneDateTimeToDisplayTime(date: Date, timezone?: string): Date {
        return DateUtils.toDisplayDate(date, timezone);
    }

    /**
     * Returns a clone of the provided date, where the clone has the same local time as the provided date,
     * but within the current timezone configured for moment, regardless of the provided date's timezone.
     * Ex: the provided date is 3 pm with tz America/Los_Angeles. moment.tz.setDefault is configured with America/New_York
     * the returned value will be 3 pm wih tz America/New_York
     * @param date
     */
    public static cloneMomentToConfiguredTimezone(date: Moment): Moment {
        const clonedDate = moment().year(date.year()).month(date.month()).date(date.date()).hour(date.hour()).minutes(date.minutes()).seconds(date.seconds()).milliseconds(date.milliseconds());
        return clonedDate;
    }

    /**
     * Returns a clone of the provided date, where the clone is in UTC but has the same date time values as the provided date,
     * regardless of its timezone
     * @param date
     */
    public static cloneTimezoneMomentToUtcMoment(date: Moment): Moment {
        const clonedDateUtc = moment.utc().year(date.year()).month(date.month()).date(date.date()).hour(date.hour()).minutes(date.minutes()).seconds(date.seconds()).milliseconds(date.milliseconds());
        return clonedDateUtc;
    }

    /** @deprecated Use getDifferenceInDays instead. Kept with original Moment implementation for equivalence verification. */
    public static getDifferenceInDaysFromMoments(startTime: moment.Moment, endTime: moment.Moment, precise: boolean = true) {
        return endTime.diff(startTime, 'days', precise);
    }

    /** @deprecated Use getElapsedHours instead. Kept with original Moment implementation for equivalence verification. */
    public static getDifferenceInHoursFromMoments(startTime: moment.Moment, endTime: moment.Moment, precise: boolean = true) {
        return endTime.diff(startTime, 'hours', precise);
    }

    /** @deprecated Use getElapsedMinutes instead. Kept with original Moment implementation for equivalence verification. */
    public static getDifferenceInMinutesFromMoments(startTime: moment.Moment, endTime: moment.Moment, precise: boolean = true) {
        return endTime.diff(startTime, 'minutes', precise);
    }

    /**
     * Return a rounded clone of the given moment. Round the minutes as follows:
     * 0-7 ->   0 min
     * 8-22 ->  15 min
     * 23-37 -> 30 min
     * 38-52 -> 45 min
     * 53-59 -> 60 min (minutes will be 0 and the hour will be incremented)
     * @param mom
     */
    public static roundMomentToQuarterHours(mom: Moment) {
        let adjustedMoment = mom.clone();
        let minutes = mom.minutes();
        if (0 <= minutes && minutes <= 7) {
            minutes = 0;
        } else if ( 8 <= minutes && minutes <= 22 ) {
            minutes = 15;
        } else if ( 23 <= minutes && minutes <= 37 ) {
            minutes = 30;
        } else if ( 38 <= minutes && minutes <= 52 ) {
            minutes = 45;
        } else if (53 <= minutes && minutes <= 59 ) {
            minutes = 60;
        }
        adjustedMoment.minutes(minutes);
        adjustedMoment.seconds(0); // ignore seconds
        adjustedMoment.milliseconds(0); // ignore miliseconds
        return adjustedMoment;
    }

    /** @deprecated Use isSameDay instead. Kept with original Moment implementation for equivalence verification. */
    public static areSameDates(date: Moment, otherDate: Moment) {
        return date.isSame(otherDate, 'day');
    }

    /** @deprecated Use areTimesIdentical instead. Kept with original Moment implementation for equivalence verification. */
    public static isSameMoment(date: Moment, otherDate: Moment): boolean {
        if ((date && otherDate && date.isSame(otherDate)) || (!date && !otherDate)) {
            return true;
        }
        return false;
    }

    /**
     * Returns true if the event overlaps, ends, or begins within time range provided
     * @param eventStart
     * @param eventEnd
     * @param rangeStart
     * @param rangeEnd
     * @param includeStartEdge If true, the event can end at the start of the range and still count as overlapping. defaults to true.
     * @param includeEndEdge If true, the event can start at the end of the range and still count as overlapping. defaults to true.
     */
    public static overlapsStartsOrEndsBetween(eventStart: DateTimeInput, eventEnd: DateTimeInput, rangeStart: DateTimeInput, rangeEnd: DateTimeInput, includeStartEdge: boolean = true, includeEndEdge: boolean = true) {
        if (!eventStart || !eventEnd || !rangeStart || !rangeEnd) {
            return false;
        }
        const eventEndMs = toNativeDate(eventEnd).getTime();
        const eventStartMs = toNativeDate(eventStart).getTime();
        const rangeStartMs = toNativeDate(rangeStart).getTime();
        const rangeEndMs = toNativeDate(rangeEnd).getTime();
        const endValid = includeStartEdge ? eventEndMs >= rangeStartMs : eventEndMs > rangeStartMs;
        const startValid = includeEndEdge ? eventStartMs <= rangeEndMs : eventStartMs < rangeEndMs;
        return endValid && startValid;
    }

    /**
     * Returns true if the start time is within the view. Start time of the view is inclusive, end time is exclusive.
     * @param startTime
     * @param rangeStart
     * @param rangeEnd
     */
    public static isStartTimeInView(startTime: DateTimeInput, rangeStart: DateTimeInput, rangeEnd: DateTimeInput): boolean {
        if (!startTime || !rangeStart || !rangeEnd) {
            return false;
        }
        const startMs = toNativeDate(startTime).getTime();
        return toNativeDate(rangeStart).getTime() <= startMs && startMs < toNativeDate(rangeEnd).getTime();
    }

    /**
     * Return adjusted moment that reconciles the selected date with specified day view start hour time.
     * @param selectedDate
     * @param dayViewStartHr
     */
    public static getDayViewStartDateAndTime(selectedDate: Moment, dayViewStartHr: number) {
        // Clone the current date and set it to the start of the team's day view.
        let viewStartDateAndTime = selectedDate.clone().startOf('day').add(dayViewStartHr, 'hours');
        return viewStartDateAndTime;
    }

    /**
     * Return adjusted moment that reconciles the selected date with specified day view end hour time.
     * @param selectedDate
     * @param dayViewEndHr
     */
    public static getDayViewEndDateAndTime(selectedDate: Moment, dayViewEndHr: number) {
        // Build a moment that represents the end of the team's day view
        // We convert the desired end in hours to the desired end in a day offset in order to handle DST boundaries
        // Moment.add is handled differently for days and hours in this case. Adding hours will drop or add an hour for DST boundaries
        let viewEndDateAndTime = selectedDate.clone().startOf('day').add(dayViewEndHr / 24, 'days');
        return viewEndDateAndTime;
    }

    /**
     * Calculate a date's index value used as a key for data lookups for shifts that fall on the date.
     * PERFORMANCE - This method is slow if called many, many times. You should use fastCalculateDateIndex.
     */
    private static calculateDateIndex(date: Moment): number {
        const startOfDate = date.clone().startOf('day');
        return startOfDate.valueOf();
    }

    /**
     * PERFORMANCE - uses memoize to prevent the over-use of moment clone() and startOf().
     * The dates are repeated as either the dates of the empty cells or the start dates of shifts. They
     * are for the most part constant (they aren't the current time which is always changing) so we can memoize.
     */
     private static _fastCalculateDateIndexCache: {[key: string]: number} = {};

     public static fastCalculateDateIndex = MemoizeUtils.memoizeUtility(
         DateUtils.calculateDateIndex,
         DateUtils._fastCalculateDateIndexCache,
         <Moment>(args: Moment) => {
             return `${args.valueOf()}`;
         }
     );

    /**
     * Returns the hours rounded to atmost 2 decimal places for displaying to users
     */
    public static getHoursRoundedForDisplay(hours: number): string {
        const hoursRounded: number = Math.round(hours * 100) / 100;
        return hoursRounded.toLocaleString(StringsStore().currentLocale);
    }

    /**
     * Convert an Excel date number value to its equivalent UTC Date
     * @param {number} excelDate
     * @returns {Date} UTC date
     */
    public static convertExcelDateToJavascriptUtcDate(excelDate: number): Date {
        if (!excelDate) {
            return null;
        }

        /**
         * Using javascript Date instead of moment
         * Moment converts date to the user's local timezone while we need the date to be read as is
         * The UTC function offered by moment allows us to print the UTC time as a string
         * We however, need the date object in the right time
         */

        // 86400 = Seconds in a day
        // 25569 = Days between 1970/01/01 and 1900/01/01 (min date in Windows Excel)
        let utcDate = new Date((excelDate - 25569) * 86400 * 1000);
        let date = new Date(utcDate.getUTCFullYear(), utcDate.getUTCMonth(), utcDate.getUTCDate(), utcDate.getUTCHours(), utcDate.getUTCMinutes(), utcDate.getUTCSeconds(), utcDate.getUTCMilliseconds());
        return date;
    }

    /**
     * Compute the Date representing the end of the day for the specified date
     * @param {Moment} date
     * @returns {Moment} end of day date
     */
    public static getEndOfDayForDate(date: Moment): Moment {
        // End of the day is midnight of the following day
        let endOfDay: Moment = DateUtils.startOfNextDay(date);
        return endOfDay;
    }

    /**
     * Compute the start or end of the week that contains the specified date, taking into account the specified start day of week.
     * @param dateInWeek specified date that the containing week will be computed for
     * @param startDayOfWeek start day of week. The computed week will begin on this day of week.
     * @param doGetWeekStart true to return the start datetime of the week. false to return the end datetime of the week
     * @returns start or end datetime of the week
     */
    public static getWeekStartEndForDate(dateInWeek: Moment, startDayOfWeek: DayOfWeek, doGetWeekStart: boolean): Moment {
        let weekStartEnd: Moment = null;
        if (dateInWeek) {
            const weekLength: number = 7;
            let startDayOfWeekValue: number = startDayOfWeek;
            let dateInWeekDayOfWeek: number = dateInWeek.day();

            // Compute the day of week index to be passed to moment().day().
            // moment().day() computes the day of week based on Sunday-Saturday weeks.  So if we are dealing with weeks that start on a day
            // other than Sunday, we may need to make adjustments.
            let dayOfWeekValue: number = (dateInWeekDayOfWeek < startDayOfWeekValue) ?
                (startDayOfWeekValue - weekLength) :    // If the specified date's day of week comes before the start day of week, then we want to fetch the previous week's day of week.
                startDayOfWeekValue;                    // Otherwise the specified date's day of week is either current or upcoming.

            // Compute the end of the week if specified
            if (!doGetWeekStart) {
                dayOfWeekValue += (weekLength - 1);
            }

            weekStartEnd = dateInWeek.clone().day(dayOfWeekValue);
            // A week begins at midnight of the start date and ends at the end of day for the end date
            if (doGetWeekStart) {
                weekStartEnd.startOf("day");
            } else {
                weekStartEnd.endOf("day");
            }
        }
        return weekStartEnd;
    }

    /** Apply the specified time of day to the specified date.
     * This is used for calculating datetimes using the values from separate date and time picker controls.
     * @param date date
     * @param time time of day to apply to the date
     */
    public static applyTimeOfDayToDate(date: Moment, time: Moment): Moment {
        let resultDateTime = date.clone();
        resultDateTime.set('hour', time.get('hour'));
        resultDateTime.set('minute', time.get('minute'));
        resultDateTime.set('second', time.get('second'));
        resultDateTime.set('millisecond', time.get('millisecond'));
        return resultDateTime;
    }

    /**
     * Returns true if the times are identical. Accounts for null and undefined moments/dates.
     * @param first
     * @param second
     */
    public static areTimesIdentical(first: DateTimeInput, second: DateTimeInput) {
        if (!first || !second) {
            return !first && !second;
        }
        return toNativeDate(first).getTime() === toNativeDate(second).getTime();
    }

    /**
     * Return the number of slots in a time range, given the range and the hours per slot
     * @param startTime
     * @param endTime
     * @param hoursPerSlot
     */
    public static getNumberOfSlotsInRange(startTime: DateTimeInput, endTime: DateTimeInput, hoursPerSlot: number): number {
        if (!startTime || !endTime) {
            return 0;
        }
        // Uses non-precise day diff (truncated) matching original Moment behavior
        return DateUtils.getDifferenceInDays(toNativeDate(startTime), toNativeDate(endTime), false) * 24 / hoursPerSlot;
    }

    /**
     * Add/subtract day-based time units (ie, days, weeks, months) to the specified date, and return the beginning of the calculated time unit.
     * This can be used to calculate things like the start of the following day, and handles DST boundaries.
     * @param date
     * @param incrementAmount number of time units to add/subtract to the specified date
     * @param dayBasedUnits Moment day-based time unit (eg, 'days', 'weeks', 'months')
     */
    public static startOfIncrementedDayTimeUnit(date: Moment, incrementAmount: number, dayBasedUnits: any): Moment {
        // For weeks, work with the actual date (not moment's Sunday-based week start)
        // to preserve the day of week, then let getWeekStartEndForDate handle the correct week boundary
        const nextDayNudgeHours: number = 3;
        if (dayBasedUnits === 'weeks') {
            return date.clone().startOf('day').add(incrementAmount, dayBasedUnits).add(nextDayNudgeHours, 'hours').startOf('day');
        }
        // Handle the special case for timezones (eg, Brazil) where the DST transition occurs around midnight.
        // Moment has bugs where trying to compute the next day returns the same day instead.  Here we workaround the
        // issue by adding a few hours to ensure that we really get the next day.
        //
        // Problem scenario:
        // In Brazil's timezone, query for the end of day of Oct 14 2017 12am, which is the day before Brazil's DST transition,
        // where 12am->1am  For some reason Moment's add(1, day) to Oct 14 12am will incorrectly return Oct 14 11pm
        // instead of midnight the next day, Oct 15.
        let nextTimeUnit: Moment = date.clone().startOf(dayBasedUnits).add(incrementAmount, dayBasedUnits).add(nextDayNudgeHours, 'hours');
        nextTimeUnit.startOf(dayBasedUnits);
        return nextTimeUnit;
    }

    /**
     * Calculate the start of the following day for the specified date
     * @param date
     * @returns start of next day
     */
    public static startOfNextDay(date: Moment): Moment {
        return DateUtils.startOfIncrementedDayTimeUnit(date, 1, 'day');
    }

    /**
     * Function that checks to see if the fetch range is contiguous with the cache range
     */
    public static isRangeContiguous(cacheStart: DateTimeInput, cacheEnd: DateTimeInput, fetchStart: DateTimeInput, fetchEnd: DateTimeInput): boolean {
        if (!cacheStart || !cacheEnd || !fetchStart || !fetchEnd) {
            return false;
        }
        // adjust the end times (both fetch and cache) to end of day (midnight-next-day)
        const adjustedFetchEndMs = DateUtils.getEndOfDay(toNativeDate(fetchEnd)).getTime();
        const adjustedCacheEndMs = DateUtils.getEndOfDay(toNativeDate(cacheEnd)).getTime();

        return toNativeDate(fetchStart).getTime() <= adjustedCacheEndMs && adjustedFetchEndMs >= toNativeDate(cacheStart).getTime();
    }

    /**
     * Used to ensure that the time that is returned is boxed in by a lower bound
     * @param time
     * @param earliestTime
     */
    public static getTimeLimitedByLowerBound(time: Moment, earliestTime: Moment): Moment {
        if (!time) {
            return null;
        }

        let timeLimitedByBound: Moment = time.clone();

        if (earliestTime && time.isBefore(earliestTime)) {
            timeLimitedByBound = earliestTime.clone();
        }

        return timeLimitedByBound;
    }

    /**
     * Used to ensure that the time that is returned is boxed in by an upper bound
     * @param time
     * @param latestTime
     */
    public static getTimeLimitedByUpperBound(time: Moment, latestTime: Moment): Moment {
        if (!time) {
            return null;
        }
        let timeLimitedByBound: Moment = time.clone();

        if (latestTime && time.isAfter(latestTime)) {
            timeLimitedByBound = latestTime.clone();
        }

        return timeLimitedByBound;
    }

    /**
     * Check whether shift time has crossed over a day
     * @param startDateTime
     * @param endDateTime
     */
    public static isCrossOverDay(startDateTime: DateTimeInput, endDateTime: DateTimeInput): boolean {
        if (!startDateTime || !endDateTime) {
            return false;
        }
        const startD = toNativeDate(startDateTime);
        const endD = toNativeDate(endDateTime);
        return !dateFnsIsSameDay(utcToZonedTime(startD, getDisplayTimezone()), utcToZonedTime(endD, getDisplayTimezone())) && endD.getTime() > startD.getTime();
    }

    /**
     * Check whether datetime is same or after the current date,
     * precision is in date
     * @param dateTime
     */
    public static isDateNowOrFuture(dateTime: DateTimeInput): boolean {
        if (!dateTime) {
            return false;
        }
        const d = toNativeDate(dateTime);
        const now = new Date();
        const timezone = getDisplayTimezone();
        return dateFnsIsSameDay(utcToZonedTime(d, timezone), utcToZonedTime(now, timezone)) || d.getTime() > now.getTime();
    }

    private static _isNowOrFutureCache: {[key: string]: boolean} = {};
    public static fastIsDateNowOrFuture = MemoizeUtils.memoizeUtility(
        DateUtils.isDateNowOrFuture,
        DateUtils._isNowOrFutureCache,
        <T>(args: T) => {
            return args ? `${(args as any).valueOf()}` : "null";
        }
    );

    /**
     * Check whether a given shift is exactly a 24hr shift
     * For example, 8AM to 8AM shift
     * This method measures the difference in minutes to be equal to 1440.
     * @param startDateTime
     * @param endDateTime
     */
    public static is24HrShift(startDateTime: DateTimeInput, endDateTime: DateTimeInput): boolean {
        if (!startDateTime || !endDateTime) {
            return false;
        }
        const diffMinutes = Math.trunc((toNativeDate(endDateTime).getTime() - toNativeDate(startDateTime).getTime()) / MS_PER_MINUTE);
        return diffMinutes === NUMBER_OF_MINUTES_IN_ONE_DAY;
    }

    // ===================================================================
    // New DateUtils API
    // All day-level methods use TimeZoneUtils.displayTimezone (team timezone).
    // date-fns/date-fns-tz are internal implementation details — application
    // code should never import them directly (see datetime-handling.md §9).
    // ===================================================================

    // --- Parsing (String → Date) ---

    /**
     * Parse an ISO string into a UTC Date. Returns null for invalid input.
     * @example parseUTCDate("2024-03-10T14:00:00Z") → Date(2024-03-10T14:00:00Z)
     * @example parseUTCDate("invalid") → null
     * @example parseUTCDate(null) → null
     */
    public static parseUTCDate(isoString: string): Date {
        if (!isoString) {
            return null;
        }
        const date = new Date(isoString);
        if (isNaN(date.getTime())) {
            return null;
        }
        return date;
    }

    /**
     * Parse a date string as a specific timezone, returning a UTC instant.
     * Used for cross-timezone construction (e.g., availability time slots defined in a different timezone).
     * This is the only DateUtils method that takes an explicit timezone parameter.
     * @example parseDate("2024-03-10", "America/New_York") → Date(2024-03-10T05:00:00Z) // midnight EST
     * @example parseDate("2024-03-10T09:00", "Asia/Tokyo") → Date(2024-03-10T00:00:00Z) // 9 AM JST
     */
    public static parseDate(dateString: string, timezone: string): Date {
        if (!dateString || !timezone) {
            return null;
        }
        // Append T00:00 to date-only strings so they parse as local midnight, not UTC midnight.
        // Strip timezone offset suffixes (Z, +05:30) — the explicit timezone param is the source of truth.
        const normalized = (dateString.includes("T") ? dateString : dateString + "T00:00")
            .replace(/[Zz]$/, "")
            .replace(/[+-]\d{2}:\d{2}$/, "");
        const parts = normalized.split(/[-T:]/);
        const local = new Date(+parts[0], +parts[1] - 1, +parts[2], +(parts[3] || 0), +(parts[4] || 0), +(parts[5] || 0), +(parts[6] || 0));
        return zonedTimeToUtc(local, timezone);
    }

    // --- Serialization (Date → String) ---

    /**
     * Serialize to UTC date string. Replaces moment(date).utc().format("YYYY-MM-DD").
     * @example toUTCDateString(Date("2024-03-10T03:00:00Z")) → "2024-03-10"
     */
    public static toUTCDateString(date: Date): string {
        const y = date.getUTCFullYear();
        const m = String(date.getUTCMonth() + 1).padStart(2, "0");
        const d = String(date.getUTCDate()).padStart(2, "0");
        return `${y}-${m}-${d}`;
    }

    /**
     * Serialize to UTC time string. Replaces moment(date).utc().format("HH:mm:ss").
     * @example toUTCTimeString(Date("2024-03-10T14:30:45Z")) → "14:30:45"
     */
    public static toUTCTimeString(date: Date): string {
        const h = String(date.getUTCHours()).padStart(2, "0");
        const m = String(date.getUTCMinutes()).padStart(2, "0");
        const s = String(date.getUTCSeconds()).padStart(2, "0");
        return `${h}:${m}:${s}`;
    }

    /**
     * Serialize to UTC datetime string. Replaces moment(date).utc().format("YYYY-MM-DDTHH:mm:ss").
     * @example toUTCDateTimeString(Date("2024-03-10T14:30:00Z")) → "2024-03-10T14:30:00"
     */
    public static toUTCDateTimeString(date: Date): string {
        return `${DateUtils.toUTCDateString(date)}T${DateUtils.toUTCTimeString(date)}`;
    }

    /**
     * Serialize to team timezone date string. Replaces moment(date).format("YYYY-MM-DD").
     * Near midnight, UTC and team timezone dates may differ.
     * @example toDateString(Date("2024-03-10T03:00:00Z")) → "2024-03-09" // 10 PM in New York = still March 9
     */
    public static toDateString(date: Date): string {
        const zoned = utcToZonedTime(date, getDisplayTimezone());
        const y = zoned.getFullYear();
        const m = String(zoned.getMonth() + 1).padStart(2, "0");
        const d = String(zoned.getDate()).padStart(2, "0");
        return `${y}-${m}-${d}`;
    }

    /**
     * Serialize to team timezone time string. Replaces moment(date).format("HH:mm:ss").
     * @example toTimeString(Date("2024-03-10T03:00:00Z")) → "22:00:00" // in New York
     */
    public static toTimeString(date: Date): string {
        const zoned = utcToZonedTime(date, getDisplayTimezone());
        const h = String(zoned.getHours()).padStart(2, "0");
        const m = String(zoned.getMinutes()).padStart(2, "0");
        const s = String(zoned.getSeconds()).padStart(2, "0");
        return `${h}:${m}:${s}`;
    }

    /**
     * Serialize to team timezone datetime string. Replaces moment(date).format("YYYY-MM-DDTHH:mm:ss").
     * @example toDateTimeString(Date("2024-03-10T03:00:00Z")) → "2024-03-09T22:00:00" // in New York
     */
    public static toDateTimeString(date: Date): string {
        return `${DateUtils.toDateString(date)}T${DateUtils.toTimeString(date)}`;
    }

    // --- Construction (component → Date) ---

    /**
     * Build a UTC Date from components interpreted in the given timezone. Zeros seconds/ms.
     * This is the only DateUtils method that takes an explicit timezone parameter —
     * used for cross-timezone construction (e.g., availability time slots).
     * @example createDate(2024, 2, 10, 9, 0, "America/New_York") → Date(2024-03-10T14:00:00Z) // 9 AM EST
     * @example createDate(2024, 2, 10, 0, 0, "Asia/Tokyo") → Date(2024-03-09T15:00:00Z) // midnight JST
     */
    public static createDate(year: number, month: number, day: number, hour: number, minute: number, timezone: string): Date {
        const local = new Date(year, month, day, hour, minute, 0, 0);
        return zonedTimeToUtc(local, timezone);
    }

    /**
     * Keep time-of-day in team timezone, replace calendar date.
     * Used for moving a shift to a different day while keeping its start time.
     * @example setDate(march15_9am_utc, 2024, 2, 20) → march20_9am_utc // 9 AM stays 9 AM
     */
    public static setDate(date: Date, year: number, month: number, day: number): Date {
        const timezone = getDisplayTimezone();
        const zoned = utcToZonedTime(date, timezone);
        zoned.setFullYear(year, month, day);
        return zonedTimeToUtc(zoned, timezone);
    }

    /**
     * Keep calendar date in team timezone, replace time-of-day. Zeros seconds/ms.
     * Used for setting a shift's start time on its existing date.
     * @example setTimeOfDay(march15_9am_utc, 14, 30) → march15_2_30pm_utc
     */
    public static setTimeOfDay(date: Date, hour: number, minute: number): Date {
        const timezone = getDisplayTimezone();
        const zoned = utcToZonedTime(date, timezone);
        const dayStart = startOfDay(zoned);
        dayStart.setHours(hour, minute, 0, 0);
        return zonedTimeToUtc(dayStart, timezone);
    }

    // --- Day boundaries ---

    /**
     * Midnight in team timezone, returned as UTC instant.
     * TODO: Rename to getStartOfDay once getStartOfDay is removed after Moment deprecation.
     * @example getStartOfDay_New(Date("2024-03-10T14:00:00Z")) → Date("2024-03-10T05:00:00Z") // midnight EST
     */
    public static getStartOfDay_New(date: Date): Date {
        const timezone = getDisplayTimezone();
        const zoned = utcToZonedTime(date, timezone);
        return zonedTimeToUtc(startOfDay(zoned), timezone);
    }

    /**
     * Midnight of the next day in team timezone. NOT 23:59:59.999 — this codebase uses
     * midnight-next-day as "end of day" (scheduling convention).
     * @example getEndOfDay(Date("2024-03-10T14:00:00Z")) → Date("2024-03-11T05:00:00Z") // midnight next day EST
     */
    public static getEndOfDay(date: Date): Date {
        const timezone = getDisplayTimezone();
        const zoned = utcToZonedTime(date, timezone);
        const nextDayZoned = addDays(startOfDay(zoned), 1);
        return zonedTimeToUtc(nextDayZoned, timezone);
    }

    // --- Day-level arithmetic ---

    /**
     * Add calendar days in team timezone. Preserves wall-clock time across DST.
     * Pass negative to subtract.
     * @example addDays(midnight_march9_pst, 1) → midnight_march10_pst (even though only 23h due to spring forward)
     */
    public static addDays(date: Date, days: number): Date {
        const timezone = getDisplayTimezone();
        const zoned = utcToZonedTime(date, timezone);
        return zonedTimeToUtc(addDays(zoned, days), timezone);
    }

    /**
     * Add calendar weeks in team timezone. Preserves wall-clock time across DST.
     * Pass negative to subtract.
     */
    public static addWeeks(date: Date, weeks: number): Date {
        const timezone = getDisplayTimezone();
        const zoned = utcToZonedTime(date, timezone);
        return zonedTimeToUtc(addWeeks(zoned, weeks), timezone);
    }

    /**
     * Add calendar months in team timezone. Preserves wall-clock time across DST.
     * Pass negative to subtract. Clamps to end-of-month (e.g., Jan 31 + 1 month → Feb 28).
     */
    public static addMonths(date: Date, months: number): Date {
        const timezone = getDisplayTimezone();
        const zoned = utcToZonedTime(date, timezone);
        return zonedTimeToUtc(addMonths(zoned, months), timezone);
    }

    // --- Day-level comparisons ---

    /**
     * Same calendar day in team timezone?
     * @example isSameDay(march15_9am, march15_5pm) → true
     * @example isSameDay(march15_11pm_ny, march16_midnight_utc) → true // same day in NY
     */
    public static isSameDay(a: Date, b: Date): boolean {
        const timezone = getDisplayTimezone();
        return dateFnsIsSameDay(utcToZonedTime(a, timezone), utcToZonedTime(b, timezone));
    }

    /**
     * Is date between start and end? Replaces moment.isBetween().
     * @param granularity "day" (compares calendar days in team tz) or "minute" (compares UTC instants)
     * @param inclusivity "()" exclusive, "[]" inclusive, "[)" start-inclusive, "(]" end-inclusive. Default "[)".
     * @example isBetween(march15, march10, march20, "day", "[]") → true
     * @example isBetween(march10, march10, march20, "day", "(]") → false // start-exclusive
     */
    public static isBetween(date: Date, start: Date, end: Date, granularity: "day" | "minute" = "minute", inclusivity: string = "[)"): boolean {
        let dateVal: number, startVal: number, endVal: number;

        if (granularity === "day") {
            const timezone = getDisplayTimezone();
            const zonedDate = utcToZonedTime(date, timezone);
            const zonedStart = utcToZonedTime(start, timezone);
            const zonedEnd = utcToZonedTime(end, timezone);
            dateVal = startOfDay(zonedDate).getTime();
            startVal = startOfDay(zonedStart).getTime();
            endVal = startOfDay(zonedEnd).getTime();
        } else {
            dateVal = date.getTime();
            startVal = start.getTime();
            endVal = end.getTime();
        }

        const startInclusive = inclusivity[0] === "[";
        const endInclusive = inclusivity[1] === "]";
        const afterStart = startInclusive ? dateVal >= startVal : dateVal > startVal;
        const beforeEnd = endInclusive ? dateVal <= endVal : dateVal < endVal;
        return afterStart && beforeEnd;
    }

    // Note: isStartOfDay, isEndOfDay, isDateNowOrFuture, isCrossOverDay, areSameDates already
    // migrated in Bucket A above — they accept DateTimeInput and use team timezone.

    // --- Day-level diffs ---

    /**
     * Timezone-aware day diff accounting for DST via zoneDelta.
     * Midnight-to-midnight across spring-forward = 2 days (not 1.958).
     * @param precise true → fractional (3.5), false → truncated toward zero (3)
     * @example getDifferenceInDays(march9_midnight, march11_midnight) → 2 // across DST
     */
    // TODO: Simplify after Moment removal. This uses Moment's zoneDelta approach (wall-clock time elapsed)
    // which differs from zone-in + ms math (real time elapsed) for precise=true across DST:
    //   zoneDelta approach: 37 wall-clock hours / 24 = 1.5417 days
    //   zone-in approach:   36 real hours / 24 = 1.5 days
    // Both produce identical results for precise=false (truncated) and Math.ceil (all current callers).
    // The only two production callers using precise=true apply Math.ceil or use it for telemetry,
    // so the fractional difference has no practical impact. Once Moment is fully removed, consider
    // switching to the simpler zone-in approach: differenceInMilliseconds(zonedEnd, zonedStart) / MS_PER_DAY.
    public static getDifferenceInDays(start: Date, end: Date, precise: boolean = true): number {
        const timezone = getDisplayTimezone();
        const zoneDelta = getTimezoneOffsetMs(timezone, start) - getTimezoneOffsetMs(timezone, end);
        const diffMs = end.getTime() - start.getTime() - zoneDelta;
        return precise ? diffMs / MS_PER_DAY : Math.trunc(diffMs / MS_PER_DAY);
    }

    // --- Elapsed time diffs (pure UTC ms — no timezone needed) ---

    /**
     * Elapsed hours between two UTC instants. Not timezone-aware.
     * @param precise true → fractional (2.5), false → truncated toward zero (2)
     * @example getElapsedHours(9am, 11_30am) → 2.5 (precise) or 2 (truncated)
     */
    public static getElapsedHours(start: Date, end: Date, precise: boolean = true): number {
        const diffMs = end.getTime() - start.getTime();
        return precise ? diffMs / MS_PER_HOUR : Math.trunc(diffMs / MS_PER_HOUR);
    }

    /**
     * Elapsed minutes between two UTC instants. Not timezone-aware.
     * @param precise true → fractional (45.5), false → truncated toward zero (45)
     */
    public static getElapsedMinutes(start: Date, end: Date, precise: boolean = true): number {
        const diffMs = end.getTime() - start.getTime();
        return precise ? diffMs / MS_PER_MINUTE : Math.trunc(diffMs / MS_PER_MINUTE);
    }

    // --- Field access (team timezone) ---

    /** Year in team timezone. @example getYear(Date("2024-12-31T23:00:00Z")) → 2024 or 2025 depending on team tz */
    public static getYear(date: Date): number {
        return utcToZonedTime(date, getDisplayTimezone()).getFullYear();
    }

    /** Month in team timezone (0=Jan, 11=Dec). */
    public static getMonth(date: Date): number {
        return utcToZonedTime(date, getDisplayTimezone()).getMonth();
    }

    /** Day of month in team timezone (1-31). */
    public static getDate(date: Date): number {
        return utcToZonedTime(date, getDisplayTimezone()).getDate();
    }

    /** Day of week in team timezone (0=Sun, 6=Sat). */
    public static getDayOfWeek(date: Date): number {
        return utcToZonedTime(date, getDisplayTimezone()).getDay();
    }

    /** Hour in team timezone (0-23). */
    public static getHour(date: Date): number {
        return utcToZonedTime(date, getDisplayTimezone()).getHours();
    }

    /** Minute in team timezone (0-59). */
    public static getMinute(date: Date): number {
        return utcToZonedTime(date, getDisplayTimezone()).getMinutes();
    }
}